import React, { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  activeIndicatorStyle,
  containerStyle,
  dialogContainerStyle,
  imageContainerStyle,
  imageStyle,
  messageContainerStyle,
  messageTextStyle,
  nameStyle,
  seenRecentlyIndicatorStyle,
  unreadIndicatorStyle,
  separatorStyle,
  chatRequestBadge
} from './styles.ts';

import { Dialog } from '@/api/messaging/types.ts';
import { routes } from '@/navigation/routes.tsx';
import {
  DialogLabels,
  MessageLabels,
  MessageTypes,
  UserStates
} from '@/enums/apiEnums.ts';
import { useDialogsStore } from '@/store/useDialogsStore.ts';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import TwoHeartsShape from '@/components/common/svg/shapes/TwoHeartsShape.tsx';
import VerifiedBadgeShape from '@/components/common/svg/shapes/VerifiedBadgeShape.tsx';
import ReplySimple from '@/components/common/svg/ReplySimple.tsx';

type ChatItemProps = {
  dialog: Dialog;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isLast?: boolean;
};

const ChatItem = ({
  dialog,
  scrollContainerRef,
  isLast = false
}: ChatItemProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setScrollPosition } = useDialogsStore();

  const handleChatItemClick = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollTop);
    }
    navigate(generatePath(routes.chat, { id: dialog.contact.hid }));
  };

  const isOutcomeMessage = !dialog.last_message?.labels.includes(
    MessageLabels.LABEL_INBOX.toString()
  );

  const isNewMatch = dialog.last_message?.type === MessageTypes.TYPE_MATCH;
  const isPendingGift =
    dialog.last_message?.type === MessageTypes.TYPE_ICEBREAKER_GIFT;
  const isBait = dialog.last_message?.type === MessageTypes.TYPE_BAIT;
  const isSticker = dialog.last_message?.type === MessageTypes.TYPE_STICKER;
  const isSystem =
    dialog.labels?.includes(DialogLabels.LABEL_SYSTEM_CHANNEL) ?? false;

  let messageText;

  if (isNewMatch) {
    messageText = t('chats.new_match');
  } else if (isPendingGift) {
    messageText = t('chats.pending_gift');
  } else if (isSticker) {
    messageText = t('chats.sticker');
  } else if (isBait) {
    messageText = dialog.last_message?.bait_body?.message;
  } else {
    messageText = dialog.last_message?.text_body?.message;
  }

  const isUnread = useMemo(() => {
    return (
      dialog.last_message_micros !== dialog.last_inbox_read_message_micros &&
      dialog.last_message.labels.includes(MessageLabels.LABEL_INBOX)
    );
  }, [dialog.last_message_micros, dialog.last_inbox_read_message_micros]);

  const renderUserState = useCallback(() => {
    if (dialog.contact.online_state === UserStates.ONLINE_STATE_ONLINE) {
      return <div style={activeIndicatorStyle} />;
    }
    if (dialog.contact.online_state === UserStates.ONLINE_STATE_RECENTLY_SEEN) {
      return <div style={seenRecentlyIndicatorStyle} />;
    }
  }, [dialog.contact.online_state]);

  const imageSrc = isSystem ? '/logo_new.png' : dialog.contact.photo_url;

  return (
    <div>
      <div style={containerStyle} onClick={handleChatItemClick}>
        <div style={imageContainerStyle}>
          <ImageWithLoader
            key={imageSrc}
            fullWidth={false}
            style={imageStyle}
            src={imageSrc ?? ''}
            alt=""
          />
          {renderUserState()}
        </div>

        <div style={dialogContainerStyle}>
          <div style={nameStyle}>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {dialog.contact.name}
            </span>
            {isSystem && (
              <VerifiedBadgeShape
                width={20}
                height={20}
                style={{ marginLeft: -2, marginTop: -2 }}
              />
            )}
            {(isPendingGift || isBait) && (
              <span style={chatRequestBadge}>{t('chats.chat_request')}</span>
            )}
          </div>

          <div style={messageContainerStyle}>
            {isNewMatch && (
              <TwoHeartsShape
                width={24}
                height={24}
                fillColor="#bc4141"
                style={{ flexShrink: 0, marginRight: 4, display: 'block' }}
              />
            )}
            <span
              style={{
                ...messageTextStyle,
                fontWeight: isUnread || isNewMatch || isPendingGift ? 600 : 300
              }}
            >
              {isOutcomeMessage && (
                <ReplySimple
                  fillColor="#000000"
                  style={{ opacity: 0.8, marginBottom: -2, marginRight: 4 }}
                />
              )}
              {messageText?.split('\n')[0] ?? ''}
            </span>
          </div>

          {!isLast && <div style={separatorStyle} />}
        </div>

        {isUnread && <div style={unreadIndicatorStyle} />}
      </div>
    </div>
  );
};

export default ChatItem;
